import { useEffect } from 'react';
import { apiErrorMessage } from '../api/errors';
import { useUiStore } from '../stores/uiStore';
import { useAdminVaultStore } from '../stores/adminVaultStore';
import { useVaultDataStore } from '../stores/vaultDataStore';
import type { VaultPack } from '../types/domain';

export function useAdminVault() {
  const packs = useAdminVaultStore((s) => s.packs);
  const load = useAdminVaultStore((s) => s.load);
  const loadingIds = useAdminVaultStore((s) => s.loadingIds);
  const loadPacks = useAdminVaultStore((s) => s.loadPacks);
  const createPackRaw = useAdminVaultStore((s) => s.createPack);
  const updatePackRaw = useAdminVaultStore((s) => s.updatePack);
  const deletePackRaw = useAdminVaultStore((s) => s.deletePack);
  const packsPending = load.status === 'loading';
  const showToast = useUiStore((s) => s.showToast);
  const loadVaultData = useVaultDataStore((s) => s.loadVaultData);

  useEffect(() => {
    if (load.status === 'idle') void loadPacks();
  }, [load.status, loadPacks]);

  return {
    packs,
    load,
    loadingIds,
    loadPacks,
    pending: packsPending,
    async createPack(pack: VaultPack): Promise<void> {
      try {
        await createPackRaw(pack);
        void loadVaultData();
        showToast('Vault pack created');
      } catch (err) {
        showToast(apiErrorMessage(err, { preserveMessage: false }));
      }
    },
    async updatePack(id: string, patch: Partial<VaultPack>): Promise<void> {
      try {
        await updatePackRaw(id, patch);
        void loadVaultData();
        showToast('Vault pack updated');
      } catch (err) {
        showToast(apiErrorMessage(err, { preserveMessage: false }));
      }
    },
    async deletePack(id: string): Promise<void> {
      try {
        await deletePackRaw(id);
        void loadVaultData();
        showToast('Vault pack deleted');
      } catch (err) {
        showToast(apiErrorMessage(err, { preserveMessage: false }));
      }
    },
    isPending(id: string): boolean {
      return loadingIds.includes(id);
    },
  };
}
